'use client';

import React, { useMemo, useState, useCallback } from 'react';
import { useWorkspaceRouter } from '@/app/features/workspace/routing/useWorkspaceRouter';
import { useSelector } from 'react-redux';
import {
  Building2,
  BookOpen,
  Search,
  X,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/app/features/language/hooks/useTranslation';
import { useAppDescription } from '@/app/hooks/useAppDescription';
import { useTrackAppAccess } from '@/app/hooks/useTrackAppAccess';
import { useUserAppAccessWiring } from '@/app/hooks/useUserAppAccessWiring';
import { cn } from '@/lib/utils';
import { toShellRoute } from '@/app/features/workspace/utils/shellRoute';
import { workspaceHref } from '@/app/features/workspace/utils/workspaceRoute';
import { iconForApp } from '@/app/features/workspace/utils/appIcon';
import type { SysAppDto } from '@/app/services/api/sysAppService';
import type { UserAppAccessSummaryDto } from '@/app/services/api/userAppAccessService';
import {
  selectFavorites,
  selectFavoritesIsLoading,
  selectRecents,
  selectRecentsIsLoading,
} from '@/app/store/slices/userAppAccessSlice';
import FavoritesSection from './components/FavoritesSection';
import RecentsSection from './components/RecentsSection';
import TileStarButton from './components/TileStarButton';
import type { WorkspaceViewProps } from './types';

/**
 * Mobile tile-grid landing rendered inside AppShell's main slot when the
 * viewport is < md. Mirrors AppShell's activity-bar grouping (Parties / Apps /
 * Tools), reuses the same icon mapping and localized text helpers, and
 * navigates via the shared `toShellRoute` util so both surfaces stay aligned.
 *
 * See .kiro/specs/workspace-mobile-home/ for the full spec.
 */

type Tile = {
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  onClick: () => void;
  sysAppId?: number;
};

type Section = {
  id: 'parties' | 'apps' | 'tools';
  label: string;
  tiles: Tile[];
};

const MobileWorkspaceHome = React.memo(function MobileWorkspaceHome({
  tenant,
  apps,
  isLoading,
}: WorkspaceViewProps) {
  const router = useWorkspaceRouter();
  const { t } = useTranslation('workspace');
  const { t: tp } = useTranslation('partyManager');
  const { getAppText } = useAppDescription();
  const trackAppAccess = useTrackAppAccess();

  const recents = useSelector(selectRecents);
  const recentsLoading = useSelector(selectRecentsIsLoading);
  const favorites = useSelector(selectFavorites);
  const favoritesLoading = useSelector(selectFavoritesIsLoading);
  // Single tenant-logo source (spec: tenant-branding-identity).
  const tenantLogoUrl: string | null = useSelector((s: any) => s.workspace.tenantLogoUrl);

  // Scope + auth + tenant-switch wiring is shared across mobile and desktop
  // home views. See `.kiro/specs/desktop-workspace-favorites/` (D4).
  useUserAppAccessWiring({
    scope: 'workspace',
    tenantId: tenant?.tenantId ? Number(tenant.tenantId) : null,
    isAuthenticated: true,
  });

  const onRecentClick = useCallback(
    (app: UserAppAccessSummaryDto) => {
      trackAppAccess(app.id);
      const route = toShellRoute(app, workspaceHref(tenant));
      router.push(route);
    },
    [router, trackAppAccess, tenant]
  );

  const onFavoriteClick = onRecentClick;

  const [searchTerm, setSearchTerm] = useState('');

  const toTile = useCallback(
    (app: SysAppDto): Tile => {
      const text = getAppText(app.appCode, app.appName, app.description, app.id);
      const route = toShellRoute(app, workspaceHref(tenant));
      return {
        id: app.appCode,
        name: text.name,
        description: text.description,
        icon: iconForApp(app),
        onClick: () => {
          trackAppAccess(app.id);
          router.push(route);
        },
        sysAppId: app.id,
      };
    },
    [getAppText, router, trackAppAccess, tenant]
  );

  const sections = useMemo<Section[]>(() => {
    const partyTiles: Tile[] = tenant
      ? [
          {
            id: 'party-mgmt',
            name: tp('title'),
            description: tp('partyManagementDesc'),
            icon: Building2,
            onClick: () => router.push(workspaceHref(tenant, 'settings/parties')),
          },
          {
            id: 'party-defs',
            name: tp('definitions.title'),
            description: tp('partyDefinitionsDesc'),
            icon: BookOpen,
            onClick: () => router.push(workspaceHref(tenant, 'settings/party-definitions')),
          },
        ]
      : [];

    const appTiles = apps.filter((a) => a.classification === 'app').map(toTile);
    const toolTiles = apps.filter((a) => a.classification === 'tool').map(toTile);

    const all: Section[] = [
      { id: 'parties', label: t('tlt.groups.parties'), tiles: partyTiles },
      { id: 'apps', label: t('tlt.groups.apps'), tiles: appTiles },
      { id: 'tools', label: t('tlt.groups.tools'), tiles: toolTiles },
    ];

    const query = searchTerm.trim().toLowerCase();
    if (query.length === 0) {
      return all.filter((s) => s.tiles.length > 0);
    }
    const matches = (tile: Tile) =>
      tile.name.toLowerCase().includes(query) ||
      tile.description.toLowerCase().includes(query);
    return all
      .map((s) => ({ ...s, tiles: s.tiles.filter(matches) }))
      .filter((s) => s.tiles.length > 0);
  }, [apps, tenant, tp, t, router, toTile, searchTerm]);

  const showEmptyState =
    !isLoading && sections.length === 0 && searchTerm.trim().length > 0;

  return (
    <div className="space-y-6">
      <header className="flex items-center gap-3">
        {tenantLogoUrl && (
          <img
            src={tenantLogoUrl}
            alt={tenant.tenantName}
            className="h-8 w-auto object-contain rounded"
          />
        )}
        <div className="min-w-0">
          <h1
            className="font-bold text-primary truncate"
            style={{ fontSize: 'var(--text-fluid-h1)' }}
          >
            {tenant.tenantName}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">{t('subtitle')}</p>
        </div>
      </header>

      <FavoritesSection
        favorites={favorites}
        isLoading={favoritesLoading}
        onTileClick={onFavoriteClick}
      />

      <RecentsSection
        recents={recents}
        isLoading={recentsLoading}
        onTileClick={onRecentClick}
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
        <Input
          type="search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder={t('searchPlaceholder')}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          enterKeyHint="search"
          className="h-11 pl-9 pr-11 [&::-webkit-search-cancel-button]:appearance-none"
        />
        {searchTerm.length > 0 && (
          <button
            type="button"
            onClick={() => setSearchTerm('')}
            aria-label={t('clearSearch')}
            className="absolute right-1 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {isLoading && sections.length === 0 ? (
        <LoadingSections />
      ) : showEmptyState ? (
        <div className="rounded-xl border border-border/60 bg-card p-8 text-center space-y-3">
          <Search className="w-8 h-8 mx-auto text-muted-foreground" />
          <p className="text-sm text-muted-foreground">{t('noResults')}</p>
          <Button variant="outline" size="sm" onClick={() => setSearchTerm('')}>
            {t('clearSearch')}
          </Button>
        </div>
      ) : (
        sections.map((section) => (
          <section key={section.id} className="space-y-3">
            <h2 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              {section.label}
            </h2>
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {section.tiles.map((tile) => {
                const Icon = tile.icon;
                return (
                  <div key={tile.id} className="relative">
                    <button
                      type="button"
                      onClick={tile.onClick}
                      className={cn(
                        'touch-target w-full flex flex-col items-center gap-1 p-3',
                        'rounded-xl border border-border/60 bg-card',
                        'hover:bg-accent/40 active:bg-accent/60 transition-colors'
                      )}
                    >
                      <Icon className="w-6 h-6 text-primary" />
                      <span className="text-xs font-medium text-center line-clamp-1 w-full">
                        {tile.name}
                      </span>
                      {tile.description && (
                        <span className="text-[10px] text-muted-foreground text-center line-clamp-1 w-full">
                          {tile.description}
                        </span>
                      )}
                    </button>
                    {tile.sysAppId !== undefined && <TileStarButton sysAppId={tile.sysAppId} />}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}
    </div>
  );
});

MobileWorkspaceHome.displayName = 'MobileWorkspaceHome';
export default MobileWorkspaceHome;

function LoadingSections() {
  return (
    <div className="space-y-6">
      {[0, 1].map((s) => (
        <section key={s} className="space-y-3">
          <div className="h-3 w-24 rounded bg-muted animate-pulse" />
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-20 rounded-xl bg-muted animate-pulse" />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
